import { Component } from "react";
import { WarningCircle } from "@phosphor-icons/react";

/**
 * Top-level error boundary. Catches render errors in the React tree so the
 * whole app doesn't blank-page. Shows a friendly fallback with a reload link
 * and logs the error so we can investigate later.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("Unhandled UI error:", error, info?.componentStack);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF7] p-6" data-testid="app-error-boundary">
        <div className="max-w-md text-center">
          <WarningCircle size={48} weight="duotone" className="mx-auto mb-4 text-[#F59E0B]" />
          <h1 className="font-heading text-2xl font-medium mb-2">Something went wrong</h1>
          <p className="text-sm text-[#525252] mb-4">
            The portal hit an unexpected error rendering this page. Your data is safe — nothing has been lost or sent.
            Please reload the page; if the issue keeps happening, contact support and share what you were doing.
          </p>
          {this.state.error?.message && (
            <pre className="text-[11px] text-[#525252] bg-white border border-[#E5E5E5] p-3 mb-4 text-left overflow-auto max-h-32 font-mono">
              {String(this.state.error.message).slice(0, 400)}
            </pre>
          )}
          <button
            onClick={this.handleReload}
            className="bg-[#0A0A0A] text-white px-5 py-2.5 hover:bg-[#262626] text-sm font-medium"
            data-testid="error-boundary-reload"
          >
            Reload page
          </button>
        </div>
      </div>
    );
  }
}
